import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import DOMPurify from 'dompurify'
import { api } from '@/lib/api'
import { toast } from 'react-hot-toast'

interface DocSection {
  id: string
  title: string
  content: string
  category: string
}

export default function DocsPage() {
  const { t } = useTranslation('common')
  const [sections, setSections] = useState<DocSection[]>([])
  const [selectedCategory, setSelectedCategory] = useState<string>('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchDocs()
  }, [])

  const fetchDocs = async () => {
    try {
      const response = await api.get('/v1/docs/sections')
      setSections(response.data)
    } catch (_error) {
      toast.error('Failed to load documentation')
    } finally {
      setLoading(false)
    }
  }

  const categories = ['all', ...new Set(sections.map((s) => s.category))]

  const filteredSections = sections.filter((section) => {
    const matchesCategory = selectedCategory === 'all' || section.category === selectedCategory
    const matchesSearch =
      section.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      section.content.toLowerCase().includes(searchQuery.toLowerCase())
    return matchesCategory && matchesSearch
  })

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-olive-600"></div>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-charcoal-900 mb-4">{t('docs.title')}</h1>

        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <input
            type="text"
            placeholder={t('docs.search')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="flex-1 px-4 py-2 border border-olive-300 rounded-lg focus:ring-2 focus:ring-olive-500 focus:border-transparent"
          />

          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="px-4 py-2 border border-olive-300 rounded-lg focus:ring-2 focus:ring-olive-500 focus:border-transparent"
          >
            {categories.map((cat) => (
              <option key={cat} value={cat}>
                {cat === 'all' ? t('docs.allCategories') : cat}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="space-y-6">
        {filteredSections.map((section) => (
          <div
            key={section.id}
            className="bg-white rounded-lg shadow-sm border border-olive-200 p-6"
          >
            <h2 className="text-xl font-semibold text-charcoal-900 mb-2">{section.title}</h2>
            <p className="text-sm text-olive-600 mb-4">{section.category}</p>
            <div className="prose prose-olive max-w-none">
              <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(section.content) }} />
            </div>
          </div>
        ))}

        {filteredSections.length === 0 && (
          <div className="text-center py-12">
            <p className="text-charcoal-600">{t('docs.noResults')}</p>
          </div>
        )}
      </div>
    </div>
  )
}
